import type { GameMode, GameState } from "./types";
import type { DifficultyKey, WaveConfig } from "@bastion/data";
import type { WaveSystemSnapshot } from "./wave/system";

export const RUNTIME_SNAPSHOT_SCHEMA_VERSION = 1;

export interface RuntimeSessionStats {
  enemiesKilled: number;
  goldEarned: number;
}

/** 可在文件中交换的完整游戏运行态；不包含跨局聚合统计。 */
export interface RuntimeSnapshot {
  schemaVersion: number;
  mapId: string;
  difficulty: DifficultyKey;
  mode: GameMode;
  contentFingerprint: string;
  initialSeed: number;
  clockTime: number;
  randomState: number;
  state: GameState;
  currentWaveIndex: number;
  currentWaveConfig: WaveConfig | null;
  waveSystem: WaveSystemSnapshot;
  session: RuntimeSessionStats;
  towersBuiltSession: number;
  livesLostThisGame: number;
  wave1CompleteTime: number;
}

export function parseRuntimeSnapshot(source: string): RuntimeSnapshot {
  let value: unknown;
  try {
    value = JSON.parse(source) as unknown;
  } catch {
    throw new Error("文件不是有效的 JSON 格式");
  }
  if (!isRuntimeSnapshot(value)) throw new Error("文件不是受支持的游戏进度文件");
  return value;
}

export function createRuntimeSnapshotJson(snapshot: RuntimeSnapshot): string {
  return JSON.stringify(snapshot, null, 2);
}

export function isRuntimeSnapshot(value: unknown): value is RuntimeSnapshot {
  if (!isRecord(value)) return false;
  return (
    value.schemaVersion === RUNTIME_SNAPSHOT_SCHEMA_VERSION &&
    typeof value.mapId === "string" &&
    value.mapId.length > 0 &&
    (value.difficulty === "easy" || value.difficulty === "normal" || value.difficulty === "hard") &&
    (value.mode === "normal" || value.mode === "debug") &&
    typeof value.contentFingerprint === "string" &&
    value.contentFingerprint.length > 0 &&
    isFiniteNumber(value.initialSeed) &&
    isUint32(value.randomState) &&
    isFiniteNumber(value.clockTime) &&
    value.clockTime >= 0 &&
    isGameState(value.state) &&
    value.state.time <= value.clockTime &&
    value.state.maxHp > 0 &&
    value.state.baseHp <= value.state.maxHp &&
    (!value.state.waveInProgress || value.state.wave > 0) &&
    value.currentWaveIndex === value.state.wave - 1 &&
    Number.isInteger(value.currentWaveIndex) &&
    (value.currentWaveConfig === null || isWaveConfig(value.currentWaveConfig)) &&
    isWaveSystemSnapshot(value.waveSystem) &&
    (value.currentWaveConfig === null) === (value.waveSystem.currentWaveConfig === null) &&
    (value.currentWaveConfig === null ||
      value.currentWaveConfig.id === value.waveSystem.currentWaveConfig?.id) &&
    isSessionSnapshot(value.session) &&
    isNonNegativeInteger(value.towersBuiltSession) &&
    isNonNegativeNumber(value.livesLostThisGame) &&
    isNonNegativeNumber(value.wave1CompleteTime)
  );
}

export function cloneGameState(state: GameState): GameState {
  return JSON.parse(JSON.stringify(state)) as GameState;
}

function isSessionSnapshot(value: unknown): value is RuntimeSessionStats {
  return (
    isRecord(value) &&
    isNonNegativeInteger(value.enemiesKilled) &&
    isNonNegativeNumber(value.goldEarned)
  );
}

function isGameState(value: unknown): value is GameState {
  if (!isRecord(value)) return false;
  return (
    (value.status === "running" || value.status === "paused") &&
    isNonNegativeNumber(value.time) &&
    isFiniteNumber(value.gold) &&
    isNonNegativeNumber(value.baseHp) &&
    isNonNegativeNumber(value.maxHp) &&
    isNonNegativeInteger(value.wave) &&
    isNonNegativeInteger(value.totalWaves) &&
    typeof value.waveInProgress === "boolean" &&
    (!value.waveInProgress || value.wave > 0) &&
    isNonNegativeNumber(value.waveCountdown) &&
    Array.isArray(value.towers) &&
    value.towers.every(isTowerState) &&
    Array.isArray(value.enemies) &&
    value.enemies.every(isEnemyState) &&
    Array.isArray(value.projectiles) &&
    value.projectiles.every(isProjectileState) &&
    (value.gameSpeed === 1 || value.gameSpeed === 2 || value.gameSpeed === 4) &&
    isNonNegativeInteger(value.entityCounter) &&
    Array.isArray(value.skillCooldowns) &&
    value.skillCooldowns.every(
      (cooldown) =>
        isRecord(cooldown) &&
        typeof cooldown.id === "string" &&
        isNonNegativeNumber(cooldown.cooldownEnd),
    ) &&
    (value.pendingOrbitalStrikes === undefined ||
      (Array.isArray(value.pendingOrbitalStrikes) &&
        value.pendingOrbitalStrikes.every(isOrbitalStrike))) &&
    (value.queuedGlobalSkills === undefined ||
      (Array.isArray(value.queuedGlobalSkills) && value.queuedGlobalSkills.every(isQueuedSkill))) &&
    (value.activeSkillFields === undefined ||
      (Array.isArray(value.activeSkillFields) &&
        value.activeSkillFields.every(isActiveSkillField))) &&
    (value.pendingWaveReward === undefined || isPendingWaveReward(value.pendingWaveReward)) &&
    (value.isEndless === undefined || typeof value.isEndless === "boolean") &&
    isNonNegativeInteger(value.noPathSpawnCount)
  );
}

function isWaveSystemSnapshot(value: unknown): value is WaveSystemSnapshot {
  if (!isRecord(value)) return false;
  return (
    isFiniteNumber(value.waveStartTime) &&
    value.waveStartTime >= -1 &&
    isFiniteNumber(value.waveEndTime) &&
    value.waveEndTime >= -1 &&
    Number.isInteger(value.waveIndex) &&
    ((value.currentWaveConfig === null && value.waveStartTime >= -1 && value.waveEndTime >= -1) ||
      (value.currentWaveConfig !== null &&
        value.waveStartTime >= 0 &&
        value.waveEndTime >= value.waveStartTime &&
        (value.waveIndex as number) >= 0)) &&
    (value.currentWaveConfig === null || isWaveConfig(value.currentWaveConfig)) &&
    isNonNegativeInteger(value.successfullySpawnedCount)
  );
}

function isWaveConfig(value: unknown): value is WaveConfig {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    isPositiveInteger(value.wave) &&
    typeof value.mapId === "string" &&
    Array.isArray(value.groups) &&
    value.groups.every(
      (group) =>
        isRecord(group) &&
        typeof group.enemyId === "string" &&
        isPositiveInteger(group.count) &&
        isNonNegativeNumber(group.spawnInterval) &&
        isNonNegativeNumber(group.delay),
    ) &&
    (value.rewardGold === undefined || isFiniteNumber(value.rewardGold)) &&
    (value.rewardLives === undefined || isFiniteNumber(value.rewardLives)) &&
    (value.isFinal === undefined || typeof value.isFinal === "boolean")
  );
}

function isTowerState(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.type === "string" &&
    isPoint(value.position) &&
    isPositiveInteger(value.level) &&
    isNonNegativeNumber(value.cooldown) &&
    isNonNegativeNumber(value.buildCost) &&
    Array.isArray(value.upgradeCosts) &&
    value.upgradeCosts.every(isNonNegativeNumber) &&
    isRecord(value.skillReleaseProgress) &&
    Object.values(value.skillReleaseProgress).every(isNonNegativeNumber) &&
    (value.pendingAttackSkillId === undefined || typeof value.pendingAttackSkillId === "string") &&
    Array.isArray(value.attackSpeedBoosts) &&
    value.attackSpeedBoosts.every(
      (boost) =>
        isRecord(boost) &&
        isFiniteNumber(boost.factor) &&
        isNonNegativeNumber(boost.remaining) &&
        (boost.skillId === undefined || typeof boost.skillId === "string"),
    )
  );
}

function isEnemyState(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.type === "string" &&
    isNonNegativeNumber(value.hp) &&
    isNonNegativeNumber(value.maxHp) &&
    (value.speed === undefined || isNonNegativeNumber(value.speed)) &&
    (value.attack === undefined || isNonNegativeNumber(value.attack)) &&
    (value.magic === undefined || isNonNegativeNumber(value.magic)) &&
    (value.defense === undefined || isNonNegativeNumber(value.defense)) &&
    (value.magicResistance === undefined || isNonNegativeNumber(value.magicResistance)) &&
    (value.waveIndex === undefined || isNonNegativeInteger(value.waveIndex)) &&
    isPoint(value.position) &&
    isNonNegativeInteger(value.currentWaypointIndex) &&
    isNonNegativeNumber(value.segmentProgress) &&
    Array.isArray(value.waypoints) &&
    value.waypoints.every(isPoint) &&
    typeof value.alive === "boolean" &&
    typeof value.markedDead === "boolean" &&
    (value.debugStationary === undefined || typeof value.debugStationary === "boolean") &&
    (value.isBoss === undefined || typeof value.isBoss === "boolean") &&
    Array.isArray(value.statusEffects) &&
    value.statusEffects.every(isStatusEffect) &&
    (value.skillTimers === undefined ||
      (isRecord(value.skillTimers) &&
        Object.values(value.skillTimers).every(isNonNegativeNumber))) &&
    isNonNegativeNumber(value.regenTimer)
  );
}

function isStatusEffect(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.key === "string" &&
    isNonNegativeNumber(value.remaining) &&
    Array.isArray(value.modifiers) &&
    value.modifiers.every(
      (modifier) =>
        isRecord(modifier) &&
        typeof modifier.stat === "string" &&
        (modifier.mode === "FLAT" || modifier.mode === "PERCENT") &&
        isFiniteNumber(modifier.value) &&
        isNonNegativeNumber(modifier.duration),
    )
  );
}

function isPendingWaveReward(value: unknown): boolean {
  return (
    isRecord(value) &&
    isPositiveInteger(value.wave) &&
    isNonNegativeNumber(value.gold) &&
    typeof value.isFinal === "boolean"
  );
}

function isProjectileState(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.sourceTowerId === "string" &&
    typeof value.targetEnemyId === "string" &&
    isPoint(value.position) &&
    isNonNegativeNumber(value.speed) &&
    isNonNegativeNumber(value.damage) &&
    (value.damageType === "physical" || value.damageType === "magic") &&
    (value.attackMode === undefined ||
      value.attackMode === "normal" ||
      value.attackMode === "ice_cone" ||
      value.attackMode === "area_damage") &&
    (value.impactControl === undefined || isImpactControl(value.impactControl)) &&
    (value.impactAreaDamage === undefined || isImpactAreaDamage(value.impactAreaDamage)) &&
    isNonNegativeNumber(value.flightTime) &&
    typeof value.resolved === "boolean"
  );
}

function isOrbitalStrike(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    isPoint(value.position) &&
    (value.damage === undefined || isNonNegativeNumber(value.damage)) &&
    isNonNegativeNumber(value.remainingSeconds)
  );
}

function isQueuedSkill(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.skillId === "string" &&
    isPoint(value.position) &&
    (value.damage === undefined || isNonNegativeNumber(value.damage))
  );
}

function isActiveSkillField(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.skillId === "string" &&
    isPoint(value.position) &&
    isNonNegativeNumber(value.radius) &&
    isNonNegativeNumber(value.durationSeconds) &&
    isNonNegativeNumber(value.remainingSeconds) &&
    Array.isArray(value.affectedEnemyIds) &&
    value.affectedEnemyIds.every((id) => typeof id === "string")
  );
}

function isImpactControl(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonNegativeNumber(value.radius) &&
    isNonNegativeNumber(value.slowFactor) &&
    isNonNegativeNumber(value.slowDuration) &&
    isNonNegativeNumber(value.controlChance) &&
    isNonNegativeNumber(value.sourceAccuracy)
  );
}

function isImpactAreaDamage(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonNegativeNumber(value.radius) &&
    isNonNegativeNumber(value.baseAttack) &&
    (value.targetDamageMultiplier === undefined ||
      isNonNegativeNumber(value.targetDamageMultiplier)) &&
    (value.otherDamageMultiplier === undefined ||
      isNonNegativeNumber(value.otherDamageMultiplier)) &&
    (value.damageMultiplier === undefined || isNonNegativeNumber(value.damageMultiplier)) &&
    isNonNegativeNumber(value.globalDamageMultiplier) &&
    typeof value.isCritical === "boolean" &&
    isNonNegativeNumber(value.critDamage)
  );
}

function isPoint(value: unknown): boolean {
  return isRecord(value) && isFiniteNumber(value.x) && isFiniteNumber(value.y);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) > 0;
}

function isUint32(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0 && (value as number) <= 0xffffffff;
}
